import type { ExportDataSource, ExportEntityType } from "./exportApiHandlers";

export interface ExportHistoryDeepLink {
  entityType: ExportEntityType;
  source: ExportDataSource;
  exportId: string;
}

const ENTITY_TYPES: ExportEntityType[] = ["opportunities", "patients", "tasks"];

function isEntityType(value: string): value is ExportEntityType {
  return ENTITY_TYPES.some((item) => item === value);
}

function isSource(value: string): value is ExportDataSource {
  return value === "overview" || value === "configuration";
}

export function buildExportHistoryDeepLink(
  input: ExportHistoryDeepLink
): string {
  const params = new URLSearchParams({
    entity: input.entityType,
    source: input.source,
    exportId: input.exportId,
  });
  return `/export-history?${params.toString()}`;
}

export function parseExportHistoryDeepLink(
  path: string | undefined
): ExportHistoryDeepLink | null {
  if (!path) return null;
  const trimmed = path.trim();
  if (!trimmed.startsWith("/export-history")) return null;
  const query = trimmed.split("?")[1] ?? "";
  const params = new URLSearchParams(query);
  const entity = params.get("entity") ?? "";
  const source = params.get("source") ?? "overview";
  const exportId = params.get("exportId") ?? "";
  if (!isEntityType(entity) || !isSource(source) || !exportId) return null;
  return { entityType: entity, source, exportId };
}

function entityTypeFromExportType(
  exportType: string | undefined
): ExportEntityType | null {
  switch ((exportType || "").toUpperCase()) {
    case "PATIENT":
    case "PATIENTS":
      return "patients";
    case "TASK":
    case "TASKS":
      return "tasks";
    case "OPPORTUNITY":
    case "OPPORTUNITIES":
      return "opportunities";
    default:
      return null;
  }
}

function entityTypeFromTitle(
  title: string | undefined
): ExportEntityType | null {
  if (!title) return null;
  const normalized = title.toLowerCase();
  if (normalized.includes("patient")) return "patients";
  if (normalized.includes("task")) return "tasks";
  if (normalized.includes("opportunit")) return "opportunities";
  return null;
}

export type ExportNotificationHint = {
  entityType?: string;
  title?: string;
  deepLinkPath?: string;
  payload?: { exportType?: string };
};

export function exportEntityTypeFromNotification(
  input: string | undefined | ExportNotificationHint
): ExportEntityType {
  if (typeof input === "string" || input === undefined) {
    if (input === "PATIENT") return "patients";
    if (input === "TASK") return "tasks";
    return "opportunities";
  }

  const fromLink = parseExportHistoryDeepLink(input.deepLinkPath);
  if (fromLink) return fromLink.entityType;

  const fromPayload = entityTypeFromExportType(input.payload?.exportType);
  if (fromPayload) return fromPayload;

  const fromTitle = entityTypeFromTitle(input.title);
  if (fromTitle) return fromTitle;

  if (input.entityType === "PATIENT") return "patients";
  if (input.entityType === "TASK") return "tasks";
  return "opportunities";
}

export function resolveExportHistoryFromNotification(
  notification: ExportNotificationHint & { entityId?: string },
  tracked?: {
    exportId: string;
    entityType: ExportEntityType;
    source: ExportDataSource;
  }
): ExportHistoryDeepLink {
  const fromLink = parseExportHistoryDeepLink(notification.deepLinkPath);
  if (fromLink) return fromLink;

  const entityType =
    tracked?.entityType ?? exportEntityTypeFromNotification(notification);
  const source = tracked?.source ?? "overview";
  const exportId = notification.entityId || tracked?.exportId || "";
  return { entityType, source, exportId };
}
